// components/branches/config/appointment/GeneralSettings.jsx
import React from "react";

const GeneralSettings = ({ config, onChange, isEditMode }) => {
  const handleToggle = (field) => {
    onChange({
      ...config,
      [field]: !config[field],
    });
  };

  const handleAnalyticsToggle = () => {
    onChange({
      ...config,
      analytics: {
        ...config.analytics,
        trackMetrics: !config.analytics?.trackMetrics,
      },
    });
  };

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold mb-4">General Settings</h3>

        {/* Enable/Disable Appointments */}
        <div className="bg-gray-50 rounded-lg p-6 space-y-4">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <h4 className="font-medium">Enable Appointments</h4>
                {config.enabled && (
                  <span className="px-2 py-1 bg-green-100 text-green-700 text-xs rounded-full">
                    Active
                  </span>
                )}
              </div>
              <p className="text-sm text-gray-600 mt-1">
                Allow customers to book appointments at this branch
              </p>
            </div>
            {isEditMode ? (
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={config.enabled || false}
                  onChange={() => handleToggle("enabled")}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
              </label>
            ) : (
              <span
                className={`px-3 py-1 rounded-full text-sm font-medium ${
                  config.enabled
                    ? "bg-green-100 text-green-800"
                    : "bg-gray-100 text-gray-800"
                }`}
              >
                {config.enabled ? "Enabled" : "Disabled"}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Quick Stats (if appointments are enabled) */}
      {config.enabled && (
        <div>
          <h3 className="text-lg font-semibold mb-4">Configuration Summary</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-blue-50 rounded-lg p-4">
              <p className="text-sm text-gray-600">Auto-Approval</p>
              <p className="text-lg font-semibold">
                {config.autoApproval?.enabled ? "Enabled" : "Disabled"}
              </p>
            </div>

            <div className="bg-green-50 rounded-lg p-4">
              <p className="text-sm text-gray-600">Booking Window</p>
              <p className="text-lg font-semibold">
                {config.bookingWindow?.advanceBookingDays || 14} days
              </p>
            </div>

            <div className="bg-purple-50 rounded-lg p-4">
              <p className="text-sm text-gray-600">Daily Capacity</p>
              <p className="text-lg font-semibold">
                {config.capacityManagement?.maxAppointmentsPerDay || 20}{" "}
                appointments
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Analytics Settings */}
      {config.enabled && (
        <div>
          <h3 className="text-lg font-semibold mb-4">Analytics & Tracking</h3>
          <div className="bg-gray-50 rounded-lg p-6 space-y-4">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <h4 className="font-medium">Track Appointment Metrics</h4>
                <p className="text-sm text-gray-600 mt-1">
                  Collect data on booking rates, cancellations, and customer
                  behavior
                </p>
              </div>
              {isEditMode ? (
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={config.analytics?.trackMetrics || false}
                    onChange={handleAnalyticsToggle}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                </label>
              ) : (
                <span
                  className={`px-3 py-1 rounded-full text-sm font-medium ${
                    config.analytics?.trackMetrics
                      ? "bg-green-100 text-green-800"
                      : "bg-gray-100 text-gray-800"
                  }`}
                >
                  {config.analytics?.trackMetrics ? "Enabled" : "Disabled"}
                </span>
              )}
            </div>

            {config.analytics?.trackMetrics && (
              <div className="mt-4 pl-4 border-l-2 border-blue-500 space-y-2">
                <h5 className="font-medium text-sm">Metrics to Track:</h5>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div className="flex items-center gap-2">
                    <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                    <span>Booking Rate</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                    <span>Cancellation Rate</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                    <span>No-Show Rate</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                    <span>Conversion Rate</span>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default GeneralSettings;
